import {
  getTeamMemberListData,
  _getTMDataById,
  _getTMSelectItemDataById,
} from '../teamMembers/mockData'
import {
  getProjectListData,
  _getProjSelectItemDataById,
} from '../projects/mockData'
import {
  getLocationListData,
  _getLSelectItemDataById,
} from '../locations/mockData'
import {
  getSupplierListData,
  _getSSelectItemDataById,
} from '../suppliers/mockData'
import { getTsdListData, _getTSelectItemDataById } from '../tsds/mockData'
import {
  getOpportunityListData,
  _getOSelectItemDataById,
} from '../opportunities/mockData'
import { getOrderListData, _getMOSelectItemDataById } from '../orders/mockData'
import {
  getCompanyContactListData,
  _getCCTDataById,
} from '../companyContacts/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import {
  getTsdContactListData,
  _getTCTDataById,
} from './../tsdContacts/mockData'
import {
  getSupplierContactListData,
  _getSCTDataById,
} from './../supplierContacts/mockData'
import { getPriorityById, PriorityIds } from '~/services/selectLists/priorities'
import {
  getMeetingStatusById,
  MeetingStatusIds,
} from '~/services/selectLists/meetingStatii'
import type { IMeeting } from '~/types/meeting/models'
import type { TEntityId } from '~/types/models'

const meetingListData = undefined as unknown as IMeeting[]

export const getMeetingListData = getListDataFunctionFactory<IMeeting[]>({
  listData: meetingListData,
  storageKey: 'meetings',
  getStartingListData: getStartingMeetingListData,
  sortFunction: (a, b) => {
    if (b.createdAt < a.createdAt) {
      return -1
    }
    if (b.createdAt > a.createdAt) {
      return 1
    }
    return 0
  },
})

export async function getMeetingDataById(
  id: TEntityId
): Promise<IMeeting | undefined> {
  const list = await getMeetingListData()
  if (!list) {
    throw new Error(`meetingList returned undefined/null`)
  }
  return list.find((meeting: IMeeting) => meeting.id === id)
}

export async function getMeetingListDataByCompanyId(
  id: TEntityId
): Promise<IMeeting[]> {
  const list = await getMeetingListData()
  if (!list) {
    throw new Error(`meetingList returned undefined/null`)
  }
  return (
    list.filter(
      (meeting: IMeeting) =>
        meeting.company?.id === id || meeting.contact?.company?.id === id
    ) ?? []
  )
}

export async function getMeetingListDataByOpportunityId(
  id: TEntityId
): Promise<IMeeting[]> {
  const list = await getMeetingListData()
  if (!list) {
    throw new Error(`meetingList returned undefined/null`)
  }
  return (
    list.filter((meeting: IMeeting) => meeting.opportunity?.id === id) ?? []
  )
}

export async function getMeetingListDataByOrderId(
  id: TEntityId
): Promise<IMeeting[]> {
  const list = await getMeetingListData()
  if (!list) {
    throw new Error(`meetingList returned undefined/null`)
  }
  return (
    list.filter(
      (meeting: IMeeting) => meeting.order && meeting.order.id === id
    ) ?? []
  )
}

export async function getMeetingListDataByProjectId(
  id: TEntityId
): Promise<IMeeting[]> {
  const list = await getMeetingListData()
  if (!list) {
    throw new Error(`meetingList returned undefined/null`)
  }
  return (
    list.filter(
      (meeting: IMeeting) => meeting.project && meeting.project.id === id
    ) ?? []
  )
}

export async function getMeetingListDataByLocationId(
  id: TEntityId
): Promise<IMeeting[]> {
  const list = await getMeetingListData()
  if (!list) {
    throw new Error(`meetingList returned undefined/null`)
  }
  return (
    list.filter(
      (meeting: IMeeting) => meeting.location && meeting.location.id === id
    ) ?? []
  )
}

export async function getMeetingListDataByOwnerId(
  id: TEntityId
): Promise<IMeeting[]> {
  const list = await getMeetingListData()
  if (!list) {
    throw new Error(`meetingList returned undefined/null`)
  }
  const meetings =
    list.filter((m: IMeeting) => m.owner && m.owner.id === id) ?? []
  return meetings
}

export async function getMeetingListDataBySupplierId(
  id: TEntityId
): Promise<IMeeting[]> {
  const list = await getMeetingListData()
  if (!list) {
    throw new Error(`meetingList returned undefined/null`)
  }
  const meetings =
    list.filter(
      (m: IMeeting) => m.supplier?.id === id || m.contact?.supplier?.id === id
    ) ?? []
  return meetings
}

export async function getMeetingListDataByTsdId(
  id: TEntityId
): Promise<IMeeting[]> {
  const list = await getMeetingListData()
  if (!list) {
    throw new Error(`meetingList returned undefined/null`)
  }
  const meetings =
    list.filter(
      (m: IMeeting) => m.tsd?.id === id || m.contact?.tsd?.id === id
    ) ?? []
  return meetings
}

export async function getMeetingListDataByTeamMemberId(
  id: TEntityId
): Promise<IMeeting[]> {
  const list = await getMeetingListData()
  if (!list) {
    throw new Error(`meetingList returned undefined/null`)
  }
  const meetings =
    list.filter(
      (m: IMeeting) => m.owner?.id === id || m.assignedTo?.id === id
    ) ?? []
  return meetings
}

export async function getStartingMeetingListData(): Promise<IMeeting[]> {
  const tmList = await getTeamMemberListData()
  const projList = await getProjectListData()
  const cctList = await getCompanyContactListData()
  const sctList = await getSupplierContactListData()
  const locList = await getLocationListData()
  const sList = await getSupplierListData()
  const tList = await getTsdListData()
  const tctList = await getTsdContactListData()
  const oList = await getOpportunityListData()
  const moList = await getOrderListData()

  return [
    {
      id: 'XXM0001',
      hrid: 'M0001',
      project: _getProjSelectItemDataById('XXP0002', projList),
      contact: _getCCTDataById('XXCCT0031', cctList),
      descr: 'Meet and Greet',
      notes: 'This is the first meeting associated with E-LAN Migration',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-01-02T17:15:00.000Z',
      endDateTime: '2023-01-02T17:16:30.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      status: getMeetingStatusById(MeetingStatusIds.OPEN),
      createdAt: '2023-01-02T17:15:00.000Z',
      modifiedAt: '2023-01-02T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0002',
      hrid: 'M0002',
      project: _getProjSelectItemDataById('XXP0002', projList),
      contact: _getCCTDataById('XXCCT0031', cctList),
      descr: 'Meet and Greet',
      notes: 'This is the second meeting associated with E-LAN Migration',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-02-02T18:15:00.000Z',
      endDateTime: '2023-02-02T18:15:45.000Z',
      owner: _getTMDataById('XXTM0003', tmList),
      status: getMeetingStatusById(MeetingStatusIds.CLOSED),
      createdAt: '2023-02-02T18:15:00.000Z',
      modifiedAt: '2023-02-02T18:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0003',
      hrid: 'M0003',
      location: _getLSelectItemDataById('XXL0001', locList),
      contact: _getCCTDataById('XXCCT0001', cctList),
      descr: 'Met Project Engineer',
      notes: 'This is the first meeting associated with this location',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-01-02T19:15:00.000Z',
      endDateTime: '2023-01-02T20:16:12.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getMeetingStatusById(MeetingStatusIds.OPEN),
      createdAt: '2023-01-02T19:15:00.000Z',
      modifiedAt: '2023-01-02T19:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0004',
      hrid: 'M0004',
      location: _getLSelectItemDataById('XXL0001', locList),
      contact: _getCCTDataById('XXCCT0002', cctList),
      descr: 'Reviewed Contract',
      notes: 'This is the second meeting associated with this location',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-01-02T20:15:00.000Z',
      endDateTime: '2023-01-02T22:13:00.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getMeetingStatusById(MeetingStatusIds.CLOSED),
      createdAt: '2023-01-02T20:15:00.000Z',
      modifiedAt: '2023-01-02T20:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0005',
      hrid: 'M0005',
      contact: _getCCTDataById('XXCCT0031', cctList),
      descr: 'Discussed contract',
      notes: 'This is the first meeting associated with Robert Downy, Jr.',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-02-02T21:15:00.000Z',
      endDateTime: '2023-02-02T22:05:00.000Z',
      owner: _getTMDataById('XXTM0005', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0005', tmList),
      status: getMeetingStatusById(MeetingStatusIds.OPEN),
      createdAt: '2023-02-02T21:15:00.000Z',
      modifiedAt: '2023-01-02T21:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0006',
      hrid: 'M0006',
      contact: _getCCTDataById('XXCCT0031', cctList),
      descr: 'Signed contact',
      notes: 'This is the second meeting associated with Robert Downy, Jr.',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-01-02T22:15:00.000Z',
      endDateTime: '2023-01-02T22:24:00.000Z',
      owner: _getTMDataById('XXTM0005', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0005', tmList),
      status: getMeetingStatusById(MeetingStatusIds.CLOSED),
      createdAt: '2023-01-02T22:15:00.000Z',
      modifiedAt: '2023-01-02T22:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0007',
      hrid: 'M0007',
      supplier: _getSSelectItemDataById('XXS0002', sList),
      contact: _getSCTDataById('XXSCT0002', sctList),
      descr: 'Met new account rep',
      notes: 'This is the first meeting associated with Supplier Cogent',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-01-02T23:15:00.000Z',
      endDateTime: '2023-01-02T23:20:00.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getMeetingStatusById(MeetingStatusIds.OPEN),
      createdAt: '2023-01-02T23:15:00.000Z',
      modifiedAt: '2023-01-02T23:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0008',
      hrid: 'M0008',
      supplier: _getSSelectItemDataById('XXS0002', sList),
      contact: _getSCTDataById('XXSCT0002', sctList),
      descr: 'Dinner w/Rep',
      notes: 'This is the second meeting associated with Supplier Cogent',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-02-03T00:15:00.000Z',
      endDateTime: '2023-02-03T01:17:00.000Z',
      owner: _getTMDataById('XXTM0003', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getMeetingStatusById(MeetingStatusIds.CLOSED),
      createdAt: '2023-02-03T00:15:00.000Z',
      modifiedAt: '2023-02-28T00:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0009',
      hrid: 'M0009',
      supplier: _getSSelectItemDataById('XXS0003', sList),
      contact: _getSCTDataById('XXSCT0003', sctList),
      descr: 'Discussed partner spilt',
      notes: 'This is the first meeting associated with Supplier Comcast',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-02-03T01:15:00.000Z',
      endDateTime: '2023-02-03T01:48:00.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getMeetingStatusById(MeetingStatusIds.OPEN),
      createdAt: '2023-02-03T01:15:00.000Z',
      modifiedAt: '2023-02-28T01:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0010',
      hrid: 'M0010',
      supplier: _getSSelectItemDataById('XXS0003', sList),
      contact: _getSCTDataById('XXSCT0003', sctList),
      descr: 'Got new billing contact',
      notes: 'This is the second meeting associated with Supplier Comcast',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-02-03T02:15:00.000Z',
      endDateTime: '2023-02-03T02:26:00.000Z',
      owner: _getTMDataById('XXTM0003', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getMeetingStatusById(MeetingStatusIds.CLOSED),
      createdAt: '2023-02-03T02:15:00.000Z',
      modifiedAt: '2023-02-28T02:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0011',
      hrid: 'M0011',
      tsd: _getTSelectItemDataById('XXT0001', tList),
      contact: _getTCTDataById('XXTCT0002', tctList),
      descr: 'Met at client site',
      notes: 'This is the first meeting associated with Tsd TBI',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-02-03T03:15:00.000Z',
      endDateTime: '2023-02-03T04:30:00.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getMeetingStatusById(MeetingStatusIds.OPEN),
      createdAt: '2023-02-03T03:15:00.000Z',
      modifiedAt: '2023-02-28T03:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0012',
      hrid: 'M0012',
      tsd: _getTSelectItemDataById('XXT0001', tList),
      contact: _getTCTDataById('XXTCT0002', tctList),
      descr: 'Drinks w/rep',
      notes: 'This is the second meeting associated with Tsd TBI',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-01-02T17:15:00.000Z',
      endDateTime: '2023-01-02T18:03:00.000Z',
      owner: _getTMDataById('XXTM0003', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0003', tmList),
      status: getMeetingStatusById(MeetingStatusIds.CLOSED),
      createdAt: '2023-01-02T17:15:00.000Z',
      modifiedAt: '2023-01-02T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0013',
      hrid: 'M0013',
      opportunity: _getOSelectItemDataById('XXO0003', oList),
      contact: _getCCTDataById('XXCCT0031', cctList),
      descr: 'Stakeholder meeting',
      notes: 'This is the first meeting associated with opportunity O00003',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-02-02T17:15:00.000Z',
      endDateTime: '2023-02-02T17:45:00.000Z',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getMeetingStatusById(MeetingStatusIds.OPEN),
      createdAt: '2023-02-02T17:15:00.000Z',
      modifiedAt: '2023-02-02T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0014',
      hrid: 'M0014',
      opportunity: _getOSelectItemDataById('XXO0003', oList),
      contact: _getCCTDataById('XXCCT0031', cctList),
      descr: 'Follow-up on-site w/Engineer',
      notes: 'This is the second meeting associated with opportunity O00003',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-03-01T17:15:00.000Z',
      endDateTime: '2023-03-01T17:35:00.000Z',
      owner: _getTMDataById('XXTM0003', tmList),
      status: getMeetingStatusById(MeetingStatusIds.CLOSED),
      createdAt: '2023-03-01T17:15:00.000Z',
      modifiedAt: '2023-03-01T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0015',
      hrid: 'M0015',
      order: _getMOSelectItemDataById('XXORD0001', moList),
      contact: _getCCTDataById('XXCCT0031', cctList),
      descr: 'Sign and Shake on Deal',
      notes: 'This is the first meeting associated with order O00003',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-02-02T17:15:00.000Z',
      endDateTime: '2023-02-02T18:15:00.000Z',
      meetingLocation: 'Fancy Restaurant',
      owner: _getTMDataById('XXTM0001', tmList),
      assignedTo: _getTMSelectItemDataById('XXTM0001', tmList),
      status: getMeetingStatusById(MeetingStatusIds.OPEN),
      createdAt: '2023-02-02T17:15:00.000Z',
      modifiedAt: '2023-02-02T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXM0016',
      hrid: 'M0016',
      order: _getMOSelectItemDataById('XXORD0001', moList),
      contact: _getCCTDataById('XXCCT0031', cctList),
      descr: 'Sign and Shake on Deal (2)',
      notes: 'This is the second meeting associated with order O00003',
      priority: getPriorityById(PriorityIds.MEDIUM),
      startDateTime: '2023-03-01T17:15:00.000Z',
      endDateTime: '2023-03-01T18:25:00.000Z',
      meetingLocation: 'HQ, Suite 203',
      owner: _getTMDataById('XXTM0003', tmList),
      status: getMeetingStatusById(MeetingStatusIds.CLOSED),
      createdAt: '2023-03-01T17:15:00.000Z',
      modifiedAt: '2023-03-01T17:15:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
  ]
}
